import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Section from '../components/common/Section';
import Button from '../components/common/Button';
import { motion } from 'framer-motion';
import { Users, Palette, Code, Rocket, CheckCircle, ArrowRight, Building } from 'lucide-react';

const audiences = [
    { icon: Users, title: 'Product Teams', desc: 'Upskill your team in design thinking and AI-powered workflows.' },
    { icon: Palette, title: 'Design Teams', desc: 'Master AI design tools and modern UI/UX practices.' },
    { icon: Code, title: 'Engineering Teams', desc: 'Learn AI-assisted development and rapid prototyping.' },
    { icon: Rocket, title: 'Startups', desc: 'Train your founding team without hiring externally.' },
];

const benefits = [
    "Custom curriculum tailored to your team's needs",
    'On-site or virtual training options',
    'Team discounts for 5+ participants',
    'Post-training support and consultation',
    'Hands-on workshops with real projects',
    'Certificate of completion for all participants',
];

const trainingOptions = [
    {
        title: 'UI/UX Design for Teams',
        desc: 'Train your team in modern design practices with AI tools like Figma, Midjourney, and Galileo AI.',
        duration: '2-4 weeks',
        format: 'On-site / Virtual',
    },
    {
        title: 'App Development for Teams',
        desc: 'Upskill engineers in Flutter, React Native, and AI-assisted coding with Cursor and Copilot.',
        duration: '3-5 weeks',
        format: 'On-site / Virtual',
    },
];

const process = [
    { step: '01', title: 'Consultation', desc: "We understand your team's current skills and learning goals." },
    { step: '02', title: 'Custom Curriculum', desc: 'We design a training program tailored to your specific needs.' },
    { step: '03', title: 'Training Delivery', desc: 'Expert-led sessions with hands-on workshops and projects.' },
    { step: '04', title: 'Post-Training Support', desc: 'Ongoing consultation and resources for continued learning.' },
];

const CorporateTraining = () => {
    const [form, setForm] = useState({
        company: '',
        teamSize: '',
        interest: '',
        budget: '',
        message: '',
    });

    const handleSubmit = (e) => {
        e.preventDefault();
        alert('Thank you! Our team will contact you within 24 hours.');
        setForm({ company: '', teamSize: '', interest: '', budget: '', message: '' });
    };

    return (
        <>
            <Helmet>
                <title>Corporate Training | PyrusMedia Academy</title>
                <meta name="description" content="Custom training programs for teams in UI/UX design and app development with AI. On-site and virtual options available." />
            </Helmet>

            {/* Hero */}
            <section className="pt-32 pb-20 bg-white text-center relative overflow-hidden">
                <div className="absolute top-20 right-1/4 w-[400px] h-[400px] bg-[#bebd19]/5 rounded-full blur-[120px]" />
                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#bebd19]/30 bg-[#bebd19]/10 mb-6"
                    >
                        <Building size={16} className="text-[#bebd19]" />
                        <span className="text-sm font-semibold text-[#bebd19]">B2B Training</span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
                    >
                        Upskill Your Team in<br />
                        <span className="text-[#bebd19]">UI/UX & App Development</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-xl text-gray-500 max-w-2xl mx-auto"
                    >
                        Custom training programs designed for teams. Learn the same AI-powered workflows we use at PyrusMedia.
                    </motion.p>
                </div>
            </section>

            {/* Who It's For */}
            <Section className="bg-gray-50">
                <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Who It's For</h2>
                <div className="grid md:grid-cols-4 gap-6">
                    {audiences.map((a, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="bg-white p-6 rounded-2xl border border-gray-200 text-center hover:border-[#bebd19] transition-all"
                        >
                            <div className="w-14 h-14 rounded-full bg-[#bebd19]/10 flex items-center justify-center mx-auto mb-4">
                                <a.icon size={24} className="text-[#bebd19]" />
                            </div>
                            <h3 className="font-bold text-gray-900 mb-2">{a.title}</h3>
                            <p className="text-sm text-gray-500">{a.desc}</p>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* Benefits */}
            <Section className="bg-white">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Choose Us</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        {benefits.map((b, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.05 }}
                                className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl"
                            >
                                <CheckCircle size={20} className="text-[#bebd19] shrink-0" />
                                <span className="text-gray-700">{b}</span>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* Training Options */}
            <Section className="bg-gray-50">
                <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Training Programs</h2>
                <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                    {trainingOptions.map((opt, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.15 }}
                            className="bg-white p-8 rounded-2xl border border-gray-200 hover:border-[#bebd19] transition-all"
                        >
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">{opt.title}</h3>
                            <p className="text-gray-500 mb-6">{opt.desc}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-400">
                                <span className="flex items-center gap-1">📅 {opt.duration}</span>
                                <span className="flex items-center gap-1">📍 {opt.format}</span>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* Process */}
            <Section className="bg-white">
                <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">How It Works</h2>
                <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
                    {process.map((p, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.1 }}
                            className="text-center relative"
                        >
                            <div className="w-14 h-14 rounded-full bg-[#bebd19] text-black flex items-center justify-center text-xl font-bold mx-auto mb-4">
                                {p.step}
                            </div>
                            <h3 className="font-bold text-gray-900 mb-2">{p.title}</h3>
                            <p className="text-sm text-gray-500">{p.desc}</p>
                            {i < process.length - 1 && (
                                <ArrowRight className="absolute top-6 -right-4 text-gray-300 hidden md:block" size={20} />
                            )}
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* Contact Form */}
            <Section className="bg-gray-50">
                <div className="max-w-2xl mx-auto">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Schedule a Consultation</h2>
                    <p className="text-gray-500 text-center mb-8">Tell us about your team and we'll design a custom training program.</p>

                    <form onSubmit={handleSubmit} className="bg-white p-8 rounded-2xl border border-gray-200 space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-gray-600 text-sm mb-1">Company Name *</label>
                                <input
                                    type="text"
                                    required
                                    value={form.company}
                                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-gray-600 text-sm mb-1">Team Size *</label>
                                <select
                                    required
                                    value={form.teamSize}
                                    onChange={(e) => setForm({ ...form, teamSize: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none"
                                >
                                    <option value="">Select team size</option>
                                    <option>2-5 people</option>
                                    <option>6-15 people</option>
                                    <option>16-30 people</option>
                                    <option>30+ people</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-gray-600 text-sm mb-1">Training Interest *</label>
                                <select
                                    required
                                    value={form.interest}
                                    onChange={(e) => setForm({ ...form, interest: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none"
                                >
                                    <option value="">Select training type</option>
                                    <option>UI/UX Design for Teams</option>
                                    <option>App Development for Teams</option>
                                    <option>Both</option>
                                    <option>Custom</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-gray-600 text-sm mb-1">Budget Range</label>
                                <select
                                    value={form.budget}
                                    onChange={(e) => setForm({ ...form, budget: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none"
                                >
                                    <option value="">Select budget range</option>
                                    <option>Under ₹1 Lakh</option>
                                    <option>₹1-3 Lakh</option>
                                    <option>₹3-5 Lakh</option>
                                    <option>₹5 Lakh+</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-gray-600 text-sm mb-1">Additional Details</label>
                            <textarea
                                rows="4"
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                placeholder="Tell us about your team's current skill level and learning goals..."
                                className="w-full border border-gray-300 rounded-lg p-3 text-gray-900 focus:border-[#bebd19] focus:outline-none"
                            />
                        </div>
                        <button
                            type="submit"
                            className="w-full bg-[#bebd19] text-black font-bold py-3 rounded-full hover:bg-[#d4d325] transition-colors"
                        >
                            Schedule Consultation
                        </button>
                    </form>
                </div>
            </Section>

            {/* CTA */}
            <Section className="bg-[#bebd19]">
                <div className="text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">Invest in Your Team's Growth</h2>
                    <p className="text-black/70 text-lg mb-8 max-w-xl mx-auto">
                        Companies that invest in training see 218% higher income per employee. Start today.
                    </p>
                    <Button to="/contact" className="bg-black text-white hover:bg-gray-800">
                        Get in Touch
                    </Button>
                </div>
            </Section>
        </>
    );
};

export default CorporateTraining;
